class BotController {
  constructor(gameInterface, botId, gameState) {
    this.gameInterface = gameInterface;
    this.botId = botId;
    this.gameState = gameState;
    this.updateInterval = setInterval(() => this.update(), 100);
    
    // Bot personality traits (randomized for each bot)
    this.personality = {
      // How close to explosion the bot is willing to get (0-1, higher = more risk-taking)
      riskTolerance: 0.3 + Math.random() * 0.5,
      
      // How often the bot uses fake passes (0-1, higher = more deceptive)
      deceptionFrequency: 0.2 + Math.random() * 0.6,
      
      // Base reaction time in ms (higher = slower reactions)
      baseReactionTime: 300 + Math.random() * 700,
      
      // Variability in reaction time (higher = more inconsistent)
      reactionVariability: 100 + Math.random() * 300,
      
      // Chance to mistakenly think they have the bomb (0-1)
      mistakeChance: 0.05 + Math.random() * 0.1
    };
    
    // Action state
    this.plannedAction = null;
    this.actionTimer = 0;
    
    // Decision cooldown to prevent rapid decision changes
    this.decisionCooldown = 0;
    
    console.log(`Bot ${botId} created with personality:`, this.personality);
  }

  update() {
    if (!this.gameState || this.gameState.isGameOver) {
      return;
    }
    
    const botEntity = this.gameState.entities[this.botId];
    const bomb = this.gameState.bomb;
    
    // Skip if bot is eliminated
    if (!botEntity || botEntity.isEliminated) {
      return;
    }
    
    // Decrease decision cooldown
    if (this.decisionCooldown > 0) {
      this.decisionCooldown -= 100; // 100ms per update
    }
    
    // Check if bot has the bomb or is fooled by a fake pass
    const hasBomb = bomb.holderId === this.botId;
    const isFooled = botEntity.isFooledByFakePass;
    
    // If bot has the bomb, is fooled by a fake pass, or mistakenly thinks it has the bomb
    if ((hasBomb || isFooled || (Math.random() < this.personality.mistakeChance && !bomb.isAnimating && !bomb.isExploding)) && 
        botEntity.passCooldown <= 0 && botEntity.fakeCooldown <= 0) {
      // If we already have a planned action, execute it when timer expires
      if (this.plannedAction) {
        this.actionTimer -= 100;
        
        if (this.actionTimer <= 0) {
          // Execute the planned action
          if (this.plannedAction === 'pass') {
            this.gameInterface.sendGameEvent('pass_bomb', { userId: this.botId });
          } else if (this.plannedAction === 'fake') {
            this.gameInterface.sendGameEvent('fake_pass', { userId: this.botId });
          }
          
          // Reset planned action
          this.plannedAction = null;
        }
      } 
      // If no planned action and not on cooldown, decide what to do
      else if (this.decisionCooldown <= 0) {
        this.decideBombAction(botEntity, bomb, hasBomb, isFooled);
      }
    } else {
      // Reset planned action if we don't have the bomb anymore or cooldowns are active
      this.plannedAction = null;
    }
  }
  
  decideBombAction(botEntity, bomb, hasBomb, isFooled) {
    // Calculate how close the bomb is to exploding (0-1)
    const dangerLevel = (bomb.size - bomb.bombStartSize) / (bomb.bombExplodeSize - bomb.bombStartSize);
    
    // Check if pass is on cooldown
    const passOnCooldown = botEntity.passCooldown > 0;
    const fakeOnCooldown = botEntity.fakeCooldown > 0;
    
    // If both actions are on cooldown, we can't do anything
    if (passOnCooldown && fakeOnCooldown) {
      return;
    }
    
    // If bot actually has the bomb or is fooled by a fake pass, use normal decision logic
    if (hasBomb || isFooled) {
      // Decide whether to act based on danger level and risk tolerance
      // If fooled, the bot will act as if the danger is higher to increase chance of reaction
      const perceivedDanger = isFooled ? Math.max(dangerLevel, 0.5) : dangerLevel;
      
      if (perceivedDanger > this.personality.riskTolerance) {
        // Bot wants to act - decide between pass and fake
        let action = null;
        
        // If danger is very high, prefer real pass if available
        if (perceivedDanger > 0.8) {
          action = !passOnCooldown ? 'pass' : (!fakeOnCooldown ? 'fake' : null);
        } 
        // Otherwise, decide based on deception frequency
        else {
          const useDeception = Math.random() < this.personality.deceptionFrequency;
          
          if (useDeception && !fakeOnCooldown) {
            action = 'fake';
          } else if (!passOnCooldown) {
            action = 'pass';
          } else if (!fakeOnCooldown) {
            action = 'fake';
          }
        }
        
        if (action) {
          // Calculate reaction time with some variability
          // If fooled, react faster to simulate surprise
          const reactionMultiplier = isFooled ? 0.7 : 1.0;
          const reactionTime = this.personality.baseReactionTime * reactionMultiplier + 
                              (Math.random() * 2 - 1) * this.personality.reactionVariability;
          
          // Plan the action
          this.plannedAction = action;
          this.actionTimer = reactionTime;
          
          // Set decision cooldown to prevent rapid changes
          this.decisionCooldown = reactionTime + 200;
        }
      }
    } 
    // If bot mistakenly thinks it has the bomb, randomly choose an action
    else {
      // Only proceed if we're not on cooldown
      if (!passOnCooldown && !fakeOnCooldown) {
        // Randomly choose between pass and fake
        const action = Math.random() < 0.7 ? 'pass' : 'fake';
        
        // Use a shorter reaction time for mistakes
        const reactionTime = this.personality.baseReactionTime * 0.7 + 
                            (Math.random() * 2 - 1) * this.personality.reactionVariability;
        
        // Plan the action
        this.plannedAction = action;
        this.actionTimer = reactionTime;
        
        // Set decision cooldown to prevent rapid changes
        this.decisionCooldown = reactionTime + 300;
      }
    }
  }

  destroy() {
    clearInterval(this.updateInterval);
  }
}

export default BotController;
